import { isBackendConfigured } from '@/api/api-client';
import { fetchAztangofestBookingParticipantsList } from '@/api/aztangofest/fetchAztangofestBookingParticipantsList';
import {
  mapAzDanceSummaryParticipantToRegisteredParticipant,
  mergeCurrentUserIntoRegisteredParticipants,
} from '@/components/design/pages/registration-flow-page/registrationFlowMappers';
import { formatApiError } from '@/lib/apiErrorMessage';
import {
  extractEventBookingIdFromBookingJwt,
  getAzDanceBookingToken,
} from '@/lib/azdanceBookingSession';
import { toast } from 'sonner';
import type { RegisteredParticipant } from '@/state/stores/registrationStore';
import { useRegistrationStore } from '@/state/stores/registrationStore';

/**
 * GET /azdance/booking/participants → обновить `registeredParticipants` в Zustand.
 * Пары чемпионата (`copartnerId` / `partnerId`) **не** сливаются в одну строку: в сторе столько записей,
 * сколько участников на брони (карточки QR, activities, счётчики). Слияние в одну анкету — только в
 * {@link mapRegisteredParticipantsToParticipantFormData}.
 * @returns список из API (+ currentUser при необходимости) или `null`, если запрос не выполнялся / отменён до записи.
 */
export async function pullBookingParticipantsIntoStore(
  language: string,
  opts?: { isCancelled?: () => boolean },
): Promise<RegisteredParticipant[] | null> {
  const isCancelled = opts?.isCancelled;
  const tok = getAzDanceBookingToken()?.trim();
  const eidFromJwt = tok ? extractEventBookingIdFromBookingJwt(tok) : null;
  if (!isBackendConfigured() || !tok || !eidFromJwt) return null;
  try {
    const { participants: apiParticipants } =
      await fetchAztangofestBookingParticipantsList(language, tok);
    if (isCancelled?.()) return null;
    if (!apiParticipants?.length) return null;
    const registered = apiParticipants.map(
      mapAzDanceSummaryParticipantToRegisteredParticipant,
    );
    const merged = mergeCurrentUserIntoRegisteredParticipants(
      registered,
      useRegistrationStore.getState().currentUser,
    );
    if (isCancelled?.()) return null;
    useRegistrationStore.getState().registerParticipants(merged);
    return merged;
  } catch (err) {
    if (!isCancelled?.()) {
      toast.error(formatApiError(err));
    }
    return null;
  }
}
